import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowUpRight, Calendar, MapPin, Maximize2, Play, Youtube, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import SEO from "@/components/SEO";
import { SectionMark } from "@/components/PageHeader";
import Lightbox from "@/components/events/Lightbox";
import ShareEventButton from "@/components/events/ShareEventButton";
import TesArchiveBlock from "@/components/events/TesArchiveBlock";
import TucCalendar from "@/components/events/TucCalendar";
import { eventStatus, fmtEventDates, pick, ytId, eventUrl } from "@/components/events/eventUtils";
import EventCountdown from "@/components/events/EventCountdown";

function YouTubeBlock({ ev, lang }) {
    const en = lang === "en";
    const id = ytId(ev.video_url);
    const [play, setPlay] = useState(false);
    if (!id) return null;
    const title = pick(ev, "tagline", lang) || pick(ev, "title", lang);
    return (
        <div data-testid="event-video" className="border border-white/15 bg-abnormal-charcoal p-4 md:p-6">
            <p className="label-mono text-abnormal-acid">[ VIDEO ]</p>
            <h2 className="mt-2 font-display uppercase leading-[0.9] tracking-tight text-abnormal-paper text-2xl sm:text-3xl md:text-4xl">{title}</h2>
            <div className="mt-4 relative aspect-video bg-black border border-white/10 overflow-hidden">
                {play ? (
                    <iframe src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`} title={title} data-testid="event-video-iframe"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen
                        className="absolute inset-0 w-full h-full" />
                ) : (
                    <button type="button" onClick={() => setPlay(true)} data-testid="event-video-play" aria-label="Play"
                        className="group absolute inset-0 w-full h-full">
                        <img src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`} alt={title} loading="lazy" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                        <span className="absolute inset-0 flex items-center justify-center">
                            <span className="w-20 h-20 inline-flex items-center justify-center bg-abnormal-acid text-black border-2 border-black group-hover:scale-105 transition-transform"><Play size={30} fill="currentColor" /></span>
                        </span>
                    </button>
                )}
            </div>
            <a href={ev.video_url} target="_blank" rel="noreferrer" data-testid="event-video-youtube" className="mt-4 btn-ghost inline-flex">
                <Youtube size={15} /> WATCH ON YOUTUBE <ArrowUpRight size={12} />
            </a>
        </div>
    );
}

export default function EventDetail() {
    const { slug } = useParams();
    const { lang } = useLang();
    const en = lang === "en";
    const [ev, setEv] = useState(null);
    const [missing, setMissing] = useState(false);
    const [img, setImg] = useState(null);
    useEffect(() => {
        setEv(null); setMissing(false);
        api.get(`/events/${slug}`).then((r) => setEv(r.data)).catch(() => setMissing(true));
        window.scrollTo(0, 0);
    }, [slug]);

    if (missing) {
        return (
            <div data-testid="event-not-found" className="bg-abnormal-ink min-h-[60vh] flex flex-col items-center justify-center gap-4 px-5 text-center">
                <p className="font-display uppercase text-5xl text-abnormal-paper">404<span className="text-abnormal-acid">.</span></p>
                <p className="text-abnormal-paperDim">{en ? "Event not found." : "Evento non trovato."}</p>
                <Link to="/eventi" className="btn-ghost"><ArrowLeft size={14} /> {en ? "All events" : "Tutti gli eventi"}</Link>
            </div>
        );
    }
    if (!ev) return <div className="bg-abnormal-ink min-h-[60vh]" />;

    const status = eventStatus(ev);
    const past = status === "past";
    const title = pick(ev, "title", lang);
    const tagline = pick(ev, "tagline", lang);
    const paragraphs = pick(ev, "description", lang).split("\n").filter((p) => p.trim());
    const isTes = ev.slug === "tes-2026-monte-argentario";
    const isTuc = ev.slug.startsWith("tuscany-urban-cup");
    const regLabel = pick(ev, "registration_label", lang) || (en ? "Book your spot" : "Iscriviti ora");
    const jsonLd = {
        "@context": "https://schema.org", "@type": "SportsEvent", name: ev.title,
        startDate: ev.start_date, endDate: ev.end_date, eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        location: { "@type": "Place", name: ev.location, address: ev.location },
        image: ev.poster ? [ev.poster] : undefined, description: ev.summary || ev.description.slice(0, 300), url: eventUrl(ev),
        organizer: { "@type": "Organization", name: ev.organizer || "Abnormalworld" },
        sponsor: (ev.partners || []).map((p) => ({ "@type": "Organization", name: p })),
        ...(ev.registration_url && !past ? { offers: { "@type": "Offer", url: ev.registration_url, availability: "https://schema.org/InStock" } } : {}),
    };

    return (
        <div data-testid="event-detail-page" data-status={status} className="bg-abnormal-ink">
            <SEO title={`${title} · ${fmtEventDates(ev, lang)} · ${ev.location}`} description={pick(ev, "summary", lang)} image={ev.poster} path={`/eventi/${ev.slug}`} type="article" />
            <Helmet><script type="application/ld+json">{JSON.stringify(jsonLd)}</script></Helmet>

            <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-6 md:pt-10">
                <Link to="/eventi" data-testid="event-back" className="label-mono text-abnormal-paperDim hover:text-abnormal-acid inline-flex items-center gap-1.5"><ArrowLeft size={12} /> {en ? "Events" : "Eventi"}</Link>
            </div>

            <header className="max-w-[1400px] mx-auto px-5 md:px-10 pt-6 pb-12 md:pb-16 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                <div className="lg:col-span-7 lg:order-1">
                    <p className="flex flex-wrap items-center gap-2 label-mono">
                        <span data-testid="event-status" className={`px-2 py-1 border ${past ? "text-abnormal-paperDim border-white/20" : "bg-abnormal-acid text-black border-abnormal-acid"}`}>
                            {past ? (en ? "PAST EVENT · ARCHIVE" : "EVENTO PASSATO · ARCHIVIO") : (en ? "UPCOMING EVENT" : "PROSSIMO EVENTO")}
                        </span>
                        {!past && <EventCountdown ev={ev} lang={lang} compact testid="event-days" />}
                        {ev.series && <span className="text-abnormal-magenta">{ev.series}</span>}
                    </p>
                    <h1 data-testid="event-title" className="mt-4 font-display uppercase leading-[0.85] tracking-tighter text-abnormal-paper text-4xl sm:text-5xl lg:text-6xl">
                        {title}<span className="text-abnormal-acid">.</span>
                    </h1>
                    <p className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 font-display uppercase text-abnormal-paper text-lg md:text-2xl leading-none tracking-tight">
                        <span className="inline-flex items-center gap-2"><Calendar size={16} className="text-abnormal-acid" /> {fmtEventDates(ev, lang)}</span>
                        <span className="inline-flex items-center gap-2 text-abnormal-paperDim"><MapPin size={16} className="text-abnormal-acid" /> {ev.location}</span>
                    </p>
                    {tagline && !isTes && (
                        <p data-testid="event-tagline" className="mt-6 font-display uppercase leading-[0.9] tracking-tight text-abnormal-acid misprint text-3xl sm:text-4xl md:text-5xl">{tagline}</p>
                    )}

                    {/* Primary actions — visible above the fold on mobile too */}
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        {!past && ev.registration_url && (
                            <a href={ev.registration_url} target="_blank" rel="noreferrer" data-testid="event-register"
                                className="inline-flex items-center gap-2 bg-abnormal-magenta text-black hover:bg-abnormal-acid px-7 py-4 text-sm uppercase tracking-[0.2em] font-bold transition-colors w-full sm:w-auto justify-center">
                                {regLabel} <ArrowUpRight size={15} />
                            </a>
                        )}
                        <ShareEventButton event={{ ...ev, status }} />
                        {ev.official_url && (
                            <a href={ev.official_url} target="_blank" rel="noreferrer" data-testid="event-official" className="label-mono text-abnormal-paperDim hover:text-abnormal-acid inline-flex items-center gap-1 self-center">
                                {ev.official_label || (en ? "Official page" : "Pagina ufficiale")} <ArrowUpRight size={11} />
                            </a>
                        )}
                    </div>

                    <div data-testid="event-description" className="mt-8 space-y-3 text-abnormal-paperDim text-base md:text-lg leading-relaxed max-w-2xl">
                        {paragraphs.map((p, i) => {
                            const shout = p === p.toUpperCase() && /[A-Z]/.test(p);
                            return shout
                                ? <p key={i} className="font-display uppercase text-abnormal-paper text-2xl md:text-3xl leading-none tracking-tight pt-1">{p}</p>
                                : <p key={i}>{p}</p>;
                        })}
                    </div>

                    {ev.abnormal_role && (
                        <div data-testid="event-abnormal" className="mt-8 border-l-4 border-abnormal-magenta bg-abnormal-charcoal p-5 max-w-2xl">
                            <p className="label-mono text-abnormal-magenta">ABNORMAL · {pick(ev, "abnormal_role", lang)}</p>
                            {pick(ev, "abnormal_note", lang) && <p className="mt-2 text-abnormal-paper text-sm md:text-base leading-relaxed">{pick(ev, "abnormal_note", lang)}</p>}
                        </div>
                    )}

                    {(ev.organizer || (ev.partners || []).length > 0) && (
                        <p data-testid="event-partners" className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 label-mono text-abnormal-paperDim">
                            <Users size={12} className="text-abnormal-acid" />
                            {ev.organizer && <span>{en ? "Organised by" : "Organizzazione"}: <span className="text-abnormal-paper">{ev.organizer}</span></span>}
                            {(ev.partners || []).length > 0 && <span>{en ? "With" : "In collaborazione con"}: <span className="text-abnormal-paper">{ev.partners.join(" · ")}</span></span>}
                        </p>
                    )}
                </div>

                <figure className="lg:col-span-5 lg:order-2">
                    {ev.poster && (
                        <button type="button" onClick={() => setImg(ev.poster)} data-testid="event-poster-open" aria-label={en ? "Open poster" : "Apri locandina"}
                            className="group relative block w-full border-2 border-abnormal-paper/80 bg-black overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-abnormal-acid">
                            <img src={ev.poster} alt={title} loading="eager" decoding="async" className={`w-full h-auto block ${past ? "grayscale-[25%] group-hover:grayscale-0 transition-[filter]" : ""}`} />
                            <span className="absolute bottom-3 left-3 label-mono bg-black/85 text-abnormal-paper border border-abnormal-paper/30 px-2 py-1">{en ? "OFFICIAL POSTER" : "LOCANDINA UFFICIALE"}</span>
                            <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-abnormal-acid text-black label-mono px-2.5 py-1.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity"><Maximize2 size={12} /> {en ? "Enlarge" : "Ingrandisci"}</span>
                        </button>
                    )}
                    {(ev.gallery || []).length > 0 && (
                        <div data-testid="event-gallery" className="mt-3 grid grid-cols-2 gap-3">
                            {ev.gallery.map((g, i) => (
                                <button key={g} type="button" onClick={() => setImg(g)} data-testid={`event-gallery-${i}`} aria-label={en ? "Enlarge" : "Ingrandisci"}
                                    className={`group relative block border border-white/15 hover:border-abnormal-acid bg-black overflow-hidden transition-colors ${ev.gallery.length === 1 ? "col-span-2" : ""}`}>
                                    <img src={g} alt={`${title} ${i + 2}`} loading="lazy" className="w-full h-auto block" />
                                    <span className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 bg-abnormal-acid text-black label-mono px-2 py-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity"><Maximize2 size={11} /> {en ? "Enlarge" : "Ingrandisci"}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </figure>
            </header>

            {(ev.program || []).length > 0 && (
                <section data-testid="event-program" className="max-w-[1400px] mx-auto px-5 md:px-10 pb-12 md:pb-16">
                    <SectionMark n="02" title={en ? "Programme" : "Programma"} />
                    <div className="grid md:grid-cols-3 gap-4">
                        {ev.program.map((blk, i) => (
                            <div key={i} className={`border bg-abnormal-charcoal p-5 ${i === ev.program.length - 1 && /nota|note/i.test(blk.title) ? "border-white/10 md:col-span-3" : "border-white/15"}`}>
                                <p className="font-display uppercase text-2xl leading-none text-abnormal-paper">{pick(blk, "title", lang)}</p>
                                <div className="mt-3 space-y-2">
                                    {pick(blk, "body", lang).split("\n").filter(Boolean).map((l, j) => <p key={j} className="text-sm leading-relaxed text-abnormal-paperDim">{l}</p>)}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {ev.video_url && (
                <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-12 md:pb-16"><YouTubeBlock ev={ev} lang={lang} /></section>
            )}

            {(isTes || isTuc) && (
                <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-12 md:pb-16">
                    {isTes && <TesArchiveBlock lang={lang} onOpenImage={setImg} n="02" />}
                    {isTuc && <TucCalendar lang={lang} n="02" />}
                </section>
            )}

            <section className="border-t border-white/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 flex flex-wrap items-center justify-between gap-4">
                    <p className="font-display uppercase text-3xl md:text-5xl leading-none tracking-tight"><span className="text-abnormal-paper">Ride beyond the </span><span className="text-abnormal-acid">normal.</span></p>
                    <div className="flex flex-wrap gap-3">
                        <ShareEventButton event={{ ...ev, status }} />
                        <Link to={past ? "/eventi#past" : "/eventi"} data-testid="event-back-bottom" className="btn-ghost"><ArrowLeft size={14} /> {past ? (en ? "Past events" : "Eventi passati") : (en ? "All events" : "Tutti gli eventi")}</Link>
                    </div>
                </div>
            </section>

            <Lightbox src={img} alt={title} onClose={() => setImg(null)} testid="event-lightbox" />
        </div>
    );
}
